import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import {
  convertStringTimeToNgbTime,
  getFirstDigit,
  isEndOfDay,
  isInteger,
  isNumber,
  padNumber,
  toInteger,
} from '@shared/utils';
import { NgbTime } from './ngb-time';
import { NgbTimeAdapter } from './ngb-time-adapter';
import { NgbTimepickerI18n } from './ngb-timepicker-i18n';
import { NgbTimePickerConfig } from './ngb-timepicker.config';

const FILTER_REGEX = /[^0-9]/g;

/**
 * A directive that helps with wth picking hours, minutes and seconds.
 * Code from @ng-bootstrap/ng-bootstrap v10.0.0 (https://github.com/ng-bootstrap/ng-bootstrap)
 */
/* eslint-disable @typescript-eslint/naming-convention */
@Component({
  selector: 'app-ngb-timepicker',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './ngb-timepicker.component.html',
  styleUrls: ['./ngb-timepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgbTimePickerComponent),
      multi: true,
    },
  ],
})
export class NgbTimePickerComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('hourInput') hoursInputEl: ElementRef; // custom code
  @ViewChild('minuteInput') minutesInputEl: ElementRef; // custom code

  // custom code begin
  @Input() disabled: boolean;
  @Input() id: string;
  // custom code end

  /**
   * Whether to display 12H or 24H mode.
   */
  @Input() meridian: boolean;

  /**
   * If `true`, the spinners above and below inputs are visible.
   */
  @Input() spinners: boolean;

  /**
   * If `true`, it is possible to select seconds.
   */
  @Input() seconds: boolean;

  /**
   * If `true`, the timepicker is readonly and can't be changed.
   */
  @Input() readonlyInputs: boolean;

  /**
   * The size of inputs and buttons.
   */
  @Input() size: 'small' | 'medium' | 'large';

  _disabled: boolean;
  model: NgbTime;

  private hourStepValue: number;
  private minuteStepValue: number;
  private secondStepValue: number;

  private hourTempValue = 0;
  private minuteTempValue = 0;
  private hourKeyCount = 0;

  constructor(
    public i18n: NgbTimepickerI18n,
    private ngbTimeAdapter: NgbTimeAdapter<any>,
    private cd: ChangeDetectorRef,
    private readonly config: NgbTimePickerConfig
  ) {
    this.meridian = config.meridian;
    this.spinners = config.spinners;
    this.seconds = config.seconds;
    this.hourStep = config.hourStep;
    this.minuteStep = config.minuteStep;
    this.secondStep = config.secondStep;
    this._disabled = this.disabled ?? config.disabled;
    this.readonlyInputs = config.readonlyInputs;
    this.size = config.size;
  }

  get isSmallSize(): boolean {
    return this.size === 'small';
  }

  get isLargeSize(): boolean {
    return this.size === 'large';
  }

  /**
   * The number of hours to add/subtract when clicking hour spinners.
   */
  @Input()
  set hourStep(step: number) {
    this.hourStepValue = isInteger(step) ? step : this.config.hourStep;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get hourStep(): number {
    return this.hourStepValue;
  }

  /**
   * The number of minutes to add/subtract when clicking minute spinners.
   */
  @Input()
  set minuteStep(step: number) {
    this.minuteStepValue = isInteger(step) ? step : this.config.minuteStep;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get minuteStep(): number {
    return this.minuteStepValue;
  }

  /**
   * The number of seconds to add/subtract when clicking second spinners.
   */
  @Input()
  set secondStep(step: number) {
    this.secondStepValue = isInteger(step) ? step : this.config.secondStep;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get secondStep(): number {
    return this.secondStepValue;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_: any) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(value: any): void {
    // custom code begin
    const convertedValue = convertStringTimeToNgbTime(value);
    // custom code end

    const structValue = this.ngbTimeAdapter.fromModel(convertedValue); // original code: value
    this.model = structValue
      ? new NgbTime(structValue.hour, structValue.minute, structValue.second)
      : new NgbTime();
    if (!this.seconds && (!structValue || !isNumber(structValue.second))) {
      this.model.second = 0;
    }
    this.cd.markForCheck();
    // custom code begin
    setTimeout(() => {
      this.selectInput(this.hoursInputEl);
    });
    // custom code end
  }

  registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  changeHour(step: number): void {
    this.model.changeHour(step);
    this.initializeMinutes();
    this.propagateModelChange();
    // custom code begin
    setTimeout(() => {
      this.selectInput(this.hoursInputEl);
    });
    // custom code end
  }

  changeMinute(step: number): void {
    this.model.changeMinute(step);
    this.propagateModelChange();
    // custom code begin
    setTimeout(() => {
      this.selectInput(this.minutesInputEl);
    });
    // custom code end
  }

  changeSecond(step: number): void {
    this.model.changeSecond(step);
    this.propagateModelChange();
  }

  updateHour(newVal: string): void {
    const isPM = this.model.hour >= 12;
    const enteredHour = toInteger(newVal);
    if (
      this.meridian &&
      ((isPM && enteredHour < 12) || (!isPM && enteredHour === 12))
    ) {
      this.model.updateHour(enteredHour + 12);
    } else {
      this.model.updateHour(enteredHour);
    }
    this.propagateModelChange();
  }

  updateMinute(newVal: string): void {
    // this condition is needed, because updateMinute is called on blur (instead of on change, because the change event is not fired on iOS)
    // and it resets the minutes in the model when the displayed time is 24:00, resulting in an incorrect model value (23:00)
    if (!isEndOfDay(this.model) || +newVal !== 0) {
      this.model.updateMinute(toInteger(newVal));
      this.propagateModelChange();
    }
  }

  updateSecond(newVal: string): void {
    this.model.updateSecond(toInteger(newVal));
    this.propagateModelChange();
  }

  toggleMeridian(): void {
    if (this.meridian) {
      this.changeHour(12);
    }
  }

  // custom code begin
  formatHourInput(input: HTMLInputElement): void {
    input.value = input.value.replace(FILTER_REGEX, '');

    // when the new input is non-numeric, re-set previous value and keep hours selected
    if (!input.value) {
      input.value = this.formatHour(this.model.hour);
      this.selectInput(this.hoursInputEl);
      return;
    }

    this.initializeMinutes();

    const firstDigit = getFirstDigit(toInteger(input.value));
    if (firstDigit > 2 && this.hourTempValue === 0) {
      // >= 30
      input.value = padNumber(firstDigit);
      this.hourTempValue = 0;
      this.updateAndSelectMinutes(0);
    } else if (firstDigit > 4 && this.hourTempValue > 1) {
      // > 24
      input.value = padNumber(this.hourTempValue);
      this.model.updateHour(this.hourTempValue);
      this.hourTempValue = 0;
      this.minuteTempValue = firstDigit;
      this.updateAndSelectMinutes(firstDigit, true);
    } else {
      this.hourTempValue = this.hourTempValue * 10 + firstDigit;
      input.value = padNumber(this.hourTempValue);

      this.hourKeyCount = (this.hourKeyCount + 1) % 3;

      if (this.hourTempValue < 10 && this.hourKeyCount < 2) {
        this.selectInput(this.hoursInputEl);
      } else {
        this.hourTempValue = 0;
        this.updateAndSelectMinutes(0);
      }
    }
  }

  formatMinuteInput(input: HTMLInputElement): void {
    input.value = input.value.replace(FILTER_REGEX, '');

    // when the new input is non-numeric, re-set previous value and keep minutes selected
    if (!input.value) {
      input.value = this.formatMinSec(this.model.minute);
      this.selectInput(this.minutesInputEl);
      return;
    }

    const firstDigit = getFirstDigit(toInteger(input.value));
    if (firstDigit > 5 && this.minuteTempValue === 0) {
      input.value = padNumber(firstDigit);
      this.minuteTempValue = 0;
    } else {
      this.minuteTempValue = this.minuteTempValue * 10 + firstDigit;
      input.value = padNumber(
        this.minuteTempValue >= 60 ? firstDigit : this.minuteTempValue
      );
      if (this.minuteTempValue >= 10) {
        this.minuteTempValue = 0;
      }
    }
    this.selectInput(this.minutesInputEl);
  }

  formatSecondInput(input: HTMLInputElement): void {
    input.value = input.value.replace(FILTER_REGEX, '');
  }

  // custom code end

  formatInput(input: HTMLInputElement): void {
    input.value = input.value.replace(FILTER_REGEX, '');
  }

  formatHour(value?: number): string {
    if (isNumber(value)) {
      if (this.meridian) {
        return padNumber(value % 12 === 0 ? 12 : value % 12);
        // custom code begin
      } else if (value === 24) {
        return padNumber(value);
      } else if (isEndOfDay(this.model)) {
        return padNumber(24);
        // custom code end
      } else {
        return padNumber(value % 24);
      }
    } else {
      return padNumber(NaN);
    }
  }

  formatMinSec(value?: number): string {
    // custom code begin
    if (isEndOfDay(this.model)) {
      return padNumber(0);
    }
    // custom code end
    return padNumber(isNumber(value) ? value : NaN);
  }

  handleBlur(): void {
    this.onTouched();
  }

  selectInput(elementRef: ElementRef): void {
    // custom code start
    if (!this.disabled) {
      // setTimeout is used to ensure that the input is focused after the view has been updated
      setTimeout(() => {
        // custom code end

        (elementRef.nativeElement as HTMLInputElement).select();

        // custom code begin
        // prevent scroll when focusing the input on iOS devices
        elementRef.nativeElement.focus({ preventScroll: true });
      });
    }
    // custom code end
  }

  resetTempValues(): void {
    this.hourTempValue = 0;
    this.minuteTempValue = 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const secondsKey = 'seconds';
    if (
      changes[secondsKey] &&
      !this.seconds &&
      this.model &&
      !isNumber(this.model.second)
    ) {
      this.model.second = 0;
      this.propagateModelChange(false);
    }
  }

  private updateAndSelectMinutes(
    value: number,
    overwriteExistingValue = false
  ): void {
    if (isNaN(this.model.minute) || overwriteExistingValue) {
      this.model.updateMinute(value);
      this.cd.markForCheck();
    }

    this.hourKeyCount = 0;

    setTimeout(() => {
      this.selectInput(this.minutesInputEl);
    });
  }

  private initializeMinutes(): void {
    if (isNaN(this.model.minute)) {
      // set the minutes to 0 if they do not have a value, when first setting the hours
      this.model.updateMinute(0);
      this.cd.markForCheck();
    }
  }

  private propagateModelChange(touched = true): void {
    if (touched) {
      this.onTouched();
    }
    if (this.model.isValid(this.seconds)) {
      // original code
      // this.onChange(
      //   this.ngbTimeAdapter.toModel({
      //     hour: this.model.hour,
      //     minute: this.model.minute,
      //     second: this.model.second,
      //   })
      // );

      // custom code start
      this.onChange(this.model.toString());
      // custom code end
    } else {
      // original code
      // this.onChange(this.ngbTimeAdapter.toModel(null));

      // custom code start
      this.onChange(null);
      // custom code end
    }
  }
}
